import { Box, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { AlignLeft } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import * as Yup from "yup";
import {
  ExperienceErrorText,
  ExperienceHeader,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  getExperienceFieldSx,
  getExperienceTheme,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface TextData {
  title?: string;
  description?: string;
}

interface TextExperienceProps {
  darkMode: boolean;
  data?: TextData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: TextData) => void;
}

const stripRichText = (value: string | undefined | null): string =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const getValidationSchema = (t: any, isSeason: boolean) =>
  isSeason
    ? Yup.object({
        title: Yup.string().nullable(),
        description: Yup.string().nullable().test(
          "title-or-description",
          t("text_title_or_description_required"),
          function validateDescription() {
            const { title, description } = this.parent as TextData;
            return stripRichText(title).length > 0 || stripRichText(description).length > 0;
          },
        ),
      })
    : Yup.object({
        title: Yup.string()
          .required(t("text_title_required"))
          .min(3, t("text_title_min_length")),
        description: Yup.string()
          .required(t("description_required"))
          .min(10, t("description_min_length")),
      });

const TextExperience: React.FC<TextExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const theme = getExperienceTheme(darkMode);
  const validationSchema = getValidationSchema(t, isSeason);
  const isInternalUpdateRef = useRef(false);
  const fieldSx = getExperienceFieldSx(darkMode);

  const formik = useFormik<TextData>({
    initialValues: {
      title: data?.title ?? (isSeason ? "" : t("dummy_text_title")),
      description:
        data?.description ?? (isSeason ? "" : t("dummy_text_description")),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: !isSeason,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const newValues: TextData = {
        title: data.title ?? (isSeason ? "" : t("dummy_text_title")),
        description:
          data.description ?? (isSeason ? "" : t("dummy_text_description")),
      };
      const hasChanged = Object.keys(newValues).some(
        (key) =>
          formik.values[key as keyof TextData] !==
          newValues[key as keyof TextData]
      );
      if (hasChanged) {
        formik.setValues(newValues);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (onDataChange) {
      const timeoutId = setTimeout(() => {
        isInternalUpdateRef.current = true;
        onDataChange({
          title: formik.values.title,
          description: formik.values.description,
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={AlignLeft}
            title={t("text_experience")}
            subtitle={t("text_header_subtitle")}
          />
        )}

        <TextField
          label={
            <>
              {t("text_title")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="title"
          value={formik.values.title}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldError("title", undefined);
          }}
          onBlur={formik.handleBlur}
          fullWidth
          placeholder={
            isSeason
              ? t("text_title_placeholder_season")
              : t("text_title_placeholder")
          }
          error={Boolean(formik.touched.title && formik.errors.title)}
          helperText={
            formik.touched.title && formik.errors.title
              ? formik.errors.title
              : " "
          }
          sx={fieldSx}
        />

        <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              overflow: "hidden",
              "& .quill": {
                flex: 1,
                display: "flex",
                flexDirection: "column",
              },
              "& .ql-editor": {
                flex: 1,
                minHeight: 180,
              },
              "& .ql-toolbar": {
                bgcolor: theme.surfaceMuted,
                borderColor: theme.border,
                borderRadius: "12px 12px 0 0",
                ...(darkMode && {
                  "& .ql-stroke": { stroke: "var(--neutral-300)" },
                  "& .ql-fill": { fill: "var(--neutral-300)" },
                  "& .ql-picker": { color: "var(--neutral-300)" },
                  "& .ql-picker-options": {
                    backgroundColor: "var(--neutral-800)",
                    borderColor: "var(--neutral-600)",
                    color: "var(--neutral-100)",
                  },
                  "& button:hover .ql-stroke, & button.ql-active .ql-stroke": {
                    stroke: "var(--primary-400)",
                  },
                  "& button:hover .ql-fill, & button.ql-active .ql-fill": {
                    fill: "var(--primary-400)",
                  },
                  "& .ql-picker-label:hover, & .ql-picker-label.ql-active": {
                    color: "var(--primary-400)",
                  },
                }),
              },
              "& .ql-container": {
                bgcolor: theme.surface,
                borderColor: theme.border,
                color: theme.textPrimary,
                borderRadius: "0 0 12px 12px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              },
              "& .ql-editor.ql-blank::before": {
                color: theme.textMuted,
                fontStyle: "normal",
              },
            }}
          >
            <ReactQuill
              theme="snow"
              value={formik.values.description}
              onChange={(value) => {
                formik.setFieldValue("description", value);
                formik.setFieldError("description", undefined);
              }}
              onBlur={() => formik.setFieldTouched("description", true)}
              placeholder={t("text_description_placeholder")}
              modules={{
                toolbar: isSeason
                  ? [
                      ["bold", "italic", "underline"],
                      [{ list: "bullet" }],
                      ["link"],
                      ["clean"],
                    ]
                  : [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["blockquote", "code-block"],
                      ["link", "image", "video"],
                      [{ color: [] }, { background: [] }],
                      [{ align: [] }],
                      ["clean"],
                    ],
              }}
              formats={[
                "header",
                "bold",
                "italic",
                "underline",
                "strike",
                "list",
                "bullet",
                "blockquote",
                "code-block",
                "link",
                "image",
                "video",
                "color",
                "background",
                "align",
              ]}
              style={{ minHeight: "100%", height: "100%" }}
            />
          </Box>
        {formik.touched.description && formik.errors.description && (
          <ExperienceErrorText darkMode={darkMode} sx={{ mt: -0.5 }}>
            {formik.errors.description}
          </ExperienceErrorText>
        )}
      </ExperienceShell>
    </FormikProvider>
  );
};

export default TextExperience;
